import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCountries } from '@eliadmin/core/src/api/general/general-api';
import { JobportalAddEducationField as Field } from '@eliadmin/core/src/components/jobportal/jobportal-create-curriculum/jobportal-add-education/jobportal-add-education-form-fields';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import {
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  useResponsiveDialogStore,
} from '@eliadmin/core/src/components/ui/responsive-dialog';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { Textarea } from '@eliadmin/core/src/components/ui/textarea';
import {
  useFormValidationMessages,
  type FormValidationMessages,
} from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

export const addEducationSchema = (VM: FormValidationMessages) =>
  z
    .object({
      id: z.number().optional(),
      type: z.enum([EducationTypes.HIGH_SCHOOL, EducationTypes.UNIVERSITY, EducationTypes.COURSE]),
      name: z.string().optional(),
      faculty: z.string().optional(),
      field_of_work: z.string().optional(),
      location: z.string().optional(),

      //   country: z.string().min(1, VM.REQUIRED_FIELD_VALIDATION_MESSAGE()),

      studying_now: z.boolean(),
      startYear: z.string(),
      endYear: z.string().optional(),

      description: z.string(),
    })
    .superRefine((data, ctx) => {
      const checkField = (fieldName: any, condition = true) => {
        if (condition && !data[fieldName as keyof typeof data]) {
          ctx.addIssue({
            path: [fieldName],
            message: VM.REQUIRED_FIELD_VALIDATION_MESSAGE(),
            code: z.ZodIssueCode.custom,
          });
        }
      };

      const checkYear = (fieldName: any, condition = true) => {
        const yearInteger = parseInt(data[fieldName as keyof typeof data] as string, 10);

        if (!yearInteger) {
          ctx.addIssue({
            path: [fieldName],
            message: VM.INVALID_NUMBER_VALIDATION_MESSAGE(),
            code: z.ZodIssueCode.custom,
          });
        }

        /* get current year */
        const currentYear = new Date().getFullYear();

        if (
          condition &&
          (!data[fieldName as keyof typeof data] || yearInteger < 1900 || yearInteger > currentYear)
        ) {
          ctx.addIssue({
            path: [fieldName],
            message: VM.NOT_IN_RANGE_VALIDATION_MESSAGE(1900, currentYear),
            code: z.ZodIssueCode.custom,
          });
        }
      };

      checkYear('startYear');
      if (!data.studying_now) {
        checkYear('endYear');
      }

      if (data.type === EducationTypes.UNIVERSITY) {
        checkField('name');
        checkField('faculty');
        checkField('field_of_work');
        checkField('location');
      } else if (data.type === EducationTypes.HIGH_SCHOOL) {
        checkField('name');
        checkField('field_of_work');
        checkField('location');
      } else if (data.type === EducationTypes.COURSE) {
        checkField('name');
      }
    });

export type AddEducationSchemaType = z.infer<ReturnType<typeof addEducationSchema>>;

type JobportalAddEducationDialogProps = {
  onSubmit?: (data: AddEducationSchemaType) => void;
  onEdit?: (data: AddEducationSchemaType) => void;
  dataToEdit?: AddEducationSchemaType;
  newId?: number;
};

export enum EducationTypes {
  HIGH_SCHOOL = 'high_school',
  UNIVERSITY = 'university',
  COURSE = 'course',
}

const EducationTypesValues = [
  { label: 'text884', value: EducationTypes.UNIVERSITY },
  { label: 'text885', value: EducationTypes.HIGH_SCHOOL },
  { label: 'text886', value: EducationTypes.COURSE },
];

export const JobportalAddEducationDialog = ({
  onSubmit,
  onEdit,
  dataToEdit,
  newId,
}: JobportalAddEducationDialogProps) => {
  const { t } = useTranslations();

  const close = useResponsiveDialogStore(s => s.close);
  const locale = useLocaleStore(s => s.currentLocale);

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<z.infer<ReturnType<typeof addEducationSchema>>>({
    defaultValues: onEdit
      ? {
          id: dataToEdit?.id,
          type: dataToEdit?.type,
          faculty: dataToEdit?.faculty || '',
          field_of_work: dataToEdit?.field_of_work || '',
          location: dataToEdit?.location || '',
          name: dataToEdit?.name || '',
          studying_now: dataToEdit?.endYear ? false : true,
          startYear: dataToEdit?.startYear || '',
          endYear: dataToEdit?.endYear || '',
          description: dataToEdit?.description || '',
        }
      : {
          id: newId || 0,
          type: EducationTypes.UNIVERSITY,
          faculty: '',
          field_of_work: '',
          location: '',
          name: '',
          studying_now: false,
          startYear: '',
          endYear: '',
          description: '',
        },
    resolver: zodResolver(addEducationSchema(formValidationMessages)),
  });

  const { data: countries = { data: [] } } = useQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () => fetchCountries({ locale }),
    refetchOnWindowFocus: false,
  });

  const handleSubmit = form.handleSubmit(
    async data => {
      close(JobportalAddEducationDialog.id);
      if (onSubmit) {
        onSubmit(data);
      } else if (onEdit) {
        onEdit(data);
      }
    },
    errors => {
      // eslint-disable-next-line no-console
      if (process.env.NODE_ENV === 'development') console.error('Form errors:', errors);
    },
  );

  const educationType = form.watch('type');
  const studyingNow = form.watch('studying_now');

  return (
    <>
      <ResponsiveDialogContent
        dialogProps={{
          className:
            'max-w-screen max-h-[94vh] w-[680px] overflow-y-auto sm:max-w-[600px] md:max-w-[680px] lg:max-w-[680px] ea-jobportal-add-education-dialog__content ea-jobportal-dialog__content',
        }}
        drawerProps={{
          className:
            'pb-6 max-h-screen ea-jobportal-add-education-dialog__content ea-jobportal-dialog__content',
        }}
      >
        <form
          onSubmit={handleSubmit}
          className={cn(
            'flex flex-col gap-6 overflow-auto px-4',
            'ea-jobportal-add-education-dialog__form ea-jobportal-dialog__form',
          )}
        >
          <Form {...form}>
            <ResponsiveDialogHeader
              className={cn(
                'ea-jobportal-add-education-dialog__header ea-add-education-dialog__header ea-jobportal-dialog__header',
              )}
            >
              <ResponsiveDialogTitle
                className={cn(
                  'font-semibold',
                  'ea-jobportal-add-education-dialog__header-title ea-jobportal-dialog__header-title',
                )}
              >
                {t('text1030')}
              </ResponsiveDialogTitle>
            </ResponsiveDialogHeader>
            <div
              className={cn(
                'grid grid-cols-12 gap-x-4 gap-y-3',
                'ea-jobportal-dialog__form-content ea-jobportal-add-education-dialog__form-content',
              )}
            >
              <div className="col-span-12">
                <Field
                  control={form.control}
                  name="type"
                  type="radio"
                  className={{
                    radio: {
                      radioGroupItemsWrapper: 'flex-col gap-2 sm:flex-row',
                    },
                  }}
                  options={EducationTypesValues.map(g => ({
                    value: g.value,
                    label: t(g.label),
                  }))}
                />
              </div>
              {educationType === EducationTypes.UNIVERSITY && (
                <>
                  <Field control={form.control} name="name" label={t('text968')} />
                  <Field control={form.control} name="faculty" label={t('text969')} />
                  <Field control={form.control} name="field_of_work" label={t('text970')} />
                  <Field control={form.control} name="location" label={t('text933')} />
                </>
              )}
              {educationType === EducationTypes.HIGH_SCHOOL && (
                <>
                  <Field control={form.control} name="name" label={t('text971')} />
                  <Field control={form.control} name="field_of_work" label={t('text970')} />
                  <Field control={form.control} name="location" label={t('text933')} />
                </>
              )}
              {educationType === EducationTypes.COURSE && (
                <Field control={form.control} name="name" label={t('text972')} />
              )}
              {/* <Field
                control={form.control}
                name="country"
                type={'select'}
                label={t('text959')}
                options={countries.data.map(c => ({ label: c.name, value: c.id }))}
              /> */}
              <Separator
                className={cn(
                  'col-span-12 my-2 h-[1px] bg-border',
                  'ea-jobportal-dialog__separator ea-jobportal-add-education-dialog__separator',
                )}
              />
              <h3
                className={cn(
                  'col-span-12 text-lg font-semibold',
                  'ea-jobportal-dialog__section-header ea-jobportal-add-education-dialog__section-header',
                )}
              >
                {t('text960')}
              </h3>
              <div className="col-span-12">
                <Field
                  control={form.control}
                  name="studying_now"
                  type={'switch'}
                  label={t('text1031')}
                />
              </div>
              <Field control={form.control} name="startYear" label={t('text963')} />
              {!studyingNow && (
                <>
                  <Field control={form.control} name="endYear" label={t('text965')} />
                </>
              )}
              <Separator
                className={cn(
                  'col-span-12 my-2 h-[1px] bg-border',
                  'ea-jobportal-dialog__separator ea-jobportal-add-education-dialog__separator',
                )}
              />
              <h3
                className={cn(
                  'col-span-12 text-lg font-semibold',
                  'ea-jobportal-dialog__section-header ea-jobportal-add-education-dialog__section-header',
                )}
              >
                {t('text1032')}
              </h3>
              <p
                className={cn(
                  'col-span-12 text-muted-foreground',
                  'ea-jobportal-dialog__section-description ea-jobportal-add-education-dialog__section-description',
                )}
              >
                {t('text974')}
              </p>
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'col-span-12 mt-1',
                      'ea-jobportal-add-education-dialog__value-field',
                    )}
                  >
                    <FormControl
                      className={cn('', 'ea-jobportal-add-education-dialog__value-textarea')}
                    >
                      <Textarea {...field} rows={3} />
                    </FormControl>
                    <FormMessage
                      className={cn('', 'ea-jobportal-add-education-dialog__value-message')}
                    />
                  </FormItem>
                )}
              />
              <Separator
                className={cn(
                  'col-span-12 my-2 h-[1px] bg-border',
                  'ea-jobportal-dialog__separator ea-jobportal-add-education-dialog__separator',
                )}
              />
              <div
                className={cn(
                  'col-span-12 flex items-center justify-end gap-2',
                  'ea-jobportal-dialog__buttons-wrapper ea-jobportal-add-education-dialog__buttons-wrapper',
                )}
              >
                <Button
                  variant={'default-alt'}
                  className={cn(
                    '',
                    'ea-jobportal-dialog__cancel-button ea-jobportal-add-education-dialog__cancel-button',
                  )}
                  onClick={() => close(JobportalAddEducationDialog.id)}
                >
                  {t('text377')}
                </Button>
                <Button
                  className={cn(
                    '',
                    'ea-jobportal-dialog__submit-button ea-jobportal-add-education-dialog__submit-button',
                  )}
                  onClick={() => handleSubmit()}
                >
                  {t('text201')}
                </Button>
              </div>
            </div>
          </Form>
        </form>
      </ResponsiveDialogContent>
    </>
  );
};

JobportalAddEducationDialog.id = 'JOBPORTAL_ADD_EDUCATION_DIALOG';
